/* ---------------------------------------------------------------------------
   RevitalGlow - Kaching Bundles enhancements
   ---------------------------------------------------------------------------
   Loaded on every template from snippets/rg-assets.liquid, alongside
   assets/rg-bundles.css. Exits immediately when the page has no Kaching block.

   WHY EVERY TEMPLATE (looks odd until you know why)
   This used to be gated to `template.name == 'product'`. The homepage also
   carries a Kaching block (inside the buy_box section of templates/index.json)
   and so rendered unstyled. The block is a Shopify app block that can be
   dropped into any section from the theme editor, so gating by template name
   will always eventually be wrong.

   WHAT IT DOES
   1. UNIT COSTS. Both are derived from the price Kaching has already rendered
      and the day count in the bar's own wording, so both are automatically
      right in every market - US, UK and International - with no price table
      here. Kaching's own showPricesPerItem cannot do this: it divides by ITEM
      count, so the 3-bottle bundle would read "$26.65/bottle".

      Both sit side by side under the price: "$26.65/mo   $0.89/day".
      The monthly is a COMPARISON number - on its own it means nothing, its job
      is letting someone weigh the 3-month against the 1-month, so it is shown
      on BOTH bars even though on the one-month bar it equals the price. The
      duplication is deliberate; the comparison only fires if both bars are
      quoted in the same unit. im8health.com does the same.
      The per-day is a SMALLNESS number - it makes the cost feel trivial.

      The one-time link gets per-day only - it is a single purchase, so "/mo"
      would imply a recurring charge that does not exist.
   2. EMOJI POINTS. Kaching's highlight `items` are plain strings and its
      customIconGID sets ONE icon for every point in a bar, so per-point icons
      have to come from here: a point whose text starts with an emoji gets
      .rg-has-emoji and the CSS hides Kaching's tick, so you never see
      "tick + emoji" even if the tick is switched back on in the app.

   WHERE THE DAY COUNT COMES FROM (learn from this one)
   It reads the TITLE AND THE SUBTITLE TOGETHER. It used to read the subtitle
   only. On 27 Sep 2026 the bar titles were renamed to "90 Day Supply" /
   "30 Day Supply" and the subtitles emptied - at which point Kaching stops
   rendering the subtitle element at all, the day count could not be found and
   every per-day and per-month figure silently disappeared. Reading both means
   the wording can live in either field, or move between them, and this keeps
   working. Never depend on a single Kaching text field: they are owner-
   editable from the app and they move.

   SAFE DEGRADATION
   No day count anywhere in the bar's wording, or a price that cannot be
   parsed, means that bar simply gets no unit-cost line - never a wrong number.
   --------------------------------------------------------------------------- */
(function () {
  if (window.__rgBundlesInit) return;
  window.__rgBundlesInit = true;

  var ROOT = '.kaching-bundles';
  var PER_DAY_CLASS = 'rg-bundles__per-day';
  var PER_MONTH_CLASS = 'rg-bundles__per-month';
  var EMOJI_CLASS = 'rg-has-emoji';
  var DAY_RE = /(\d+)\s*day/i;
  var SEP_RE = /[•·|]/; /* Kaching separates title and price with a bullet */
  var DAYS_PER_MONTH = 30;

  /* Unicode property escapes need a modern engine. Build the regex at runtime
     so an old browser falls back instead of failing to parse the whole file. */
  var EMOJI_START;
  try {
    EMOJI_START = new RegExp('^\\s*\\p{Extended_Pictographic}', 'u');
  } catch (e) {
    EMOJI_START = /^\s*[☀-➿⬀-⯿\uD83C-\uDBFF]/;
  }

  var pending = false;
  var watched = (typeof WeakSet === 'function') ? new WeakSet() : null;

  /* ---------- money ----------
     Kaching renders a formatted string ("$79.95", "EUR 74,85", "AED 184.00").
     Parse it without assuming which separator is the decimal one, then format
     the result by reusing that string's own prefix, suffix and separator so the
     figures always match the market they appear in. Verified live against USD,
     GBP and EUR (comma decimal preserved). */

  function parseMoney(text) {
    var s = String(text).replace(/[^\d.,]/g, '');
    if (!s) return null;

    var lastDot = s.lastIndexOf('.');
    var lastComma = s.lastIndexOf(',');
    var dec = Math.max(lastDot, lastComma);
    var num;

    if (dec === -1) {
      num = parseFloat(s);
    } else if (s.length - dec - 1 === 3 && (lastDot === -1 || lastComma === -1)) {
      /* exactly three digits after the only separator: thousands, not decimals */
      num = parseFloat(s.replace(/[.,]/g, ''));
    } else {
      num = parseFloat(s.slice(0, dec).replace(/[.,]/g, '') + '.' + s.slice(dec + 1).replace(/[.,]/g, ''));
    }

    return isFinite(num) ? num : null;
  }

  function formatLike(sample, value) {
    var m = String(sample).trim().match(/^(\D*)([\d.,]+)(\D*)$/);
    if (!m) return null;

    var body = m[2];
    var lastDot = body.lastIndexOf('.');
    var lastComma = body.lastIndexOf(',');
    var dec = Math.max(lastDot, lastComma);
    var decSep = (dec !== -1 && body.length - dec - 1 === 2) ? body.charAt(dec) : '.';

    return m[1] + value.toFixed(2).replace('.', decSep) + m[3];
  }

  function unitText(rawPrice, divisor, suffix) {
    if (!divisor || divisor <= 0) return null;
    var amount = parseMoney(rawPrice);
    if (amount === null || amount <= 0) return null;
    var text = formatLike(rawPrice, amount / divisor);
    return text ? text + suffix : null;
  }

  /* Add, update or remove a line inside `host`, idempotently, so a repaint that
     changes nothing produces no DOM mutation and the observer settles.
     Placement is handled by explicit grid-area in the CSS, so the order these
     are appended in does not matter. */
  function ensureLine(host, cls, text, tag) {
    if (!host) return;
    var existing = host.querySelector('.' + cls);

    if (!text) {
      if (existing && existing.parentNode) existing.parentNode.removeChild(existing);
      return;
    }
    if (existing) {
      if (existing.textContent !== text) existing.textContent = text;
      return;
    }

    var el = document.createElement(tag || 'div');
    el.className = cls;
    el.textContent = text;
    host.appendChild(el);
  }

  /* ---------- unit costs ---------- */

  /* Title AND subtitle - see the header note. The injected figures live in the
     pricing column, never inside the title or subtitle, so they can never be
     read back as a day count on a later pass. */
  function daysFor(bar) {
    var title = bar.querySelector('.kaching-bundles__bar-title');
    var sub = bar.querySelector('.kaching-bundles__bar-subtitle');
    var wording = ((title && title.textContent) || '') + ' ' + ((sub && sub.textContent) || '');
    var m = DAY_RE.exec(wording);
    return m ? parseInt(m[1], 10) : null;
  }

  function decorateBar(bar) {
    var pricing = bar.querySelector('.kaching-bundles__bar-pricing');
    if (!pricing) return;

    var priceEl = bar.querySelector('.kaching-bundles__bar-price');
    var raw = priceEl ? (priceEl.textContent || '').trim() : null;
    var days = raw ? daysFor(bar) : null;
    var months = days ? days / DAYS_PER_MONTH : 0;

    ensureLine(pricing, PER_MONTH_CLASS, raw ? unitText(raw, months, '/mo') : null);
    ensureLine(pricing, PER_DAY_CLASS, raw ? unitText(raw, days, '/day') : null);
  }

  /* The one-time link is a single button whose text is
     "Try It Once - No AutoShip <bullet> $89.96", and its price is for the
     quantity of whichever bar is currently selected, so the days come from
     that bar rather than from the link itself.

     The per-day is added as a SIBLING of the button, never inside it: the price
     is parsed out of the button's own text, so an injected "<bullet> $1.00/day"
     inside it would be read as the price on the next pass and compound. */
  function decorateOneTime(block) {
    var wrap = block.querySelector('.kaching-bundles__subscriptions--link');
    if (!wrap) return;

    var btn = wrap.querySelector('.kaching-bundles__subscriptions__link');
    if (!btn) { ensureLine(wrap, PER_DAY_CLASS, null); return; }

    var parts = (btn.textContent || '').split(SEP_RE);
    if (parts.length < 2) { ensureLine(wrap, PER_DAY_CLASS, null); return; }

    var selected = block.querySelector('.kaching-bundles__bar--selected');
    var days = selected ? daysFor(selected) : null;
    ensureLine(wrap, PER_DAY_CLASS, unitText(parts[parts.length - 1].trim(), days, '/day'));
  }

  /* ---------- emoji points ---------- */

  function markHighlights(scope) {
    var items = scope.querySelectorAll('.kaching-bundles__highlights__item');
    for (var i = 0; i < items.length; i++) {
      var t = items[i].querySelector('.kaching-bundles__highlights__text');
      var has = !!(t && EMOJI_START.test(t.textContent || ''));
      if (has !== items[i].classList.contains(EMOJI_CLASS)) {
        items[i].classList.toggle(EMOJI_CLASS, has);
      }
    }
  }

  /* ---------- run + watch ---------- */

  function run() {
    pending = false;
    var roots = document.querySelectorAll(ROOT);
    for (var r = 0; r < roots.length; r++) {
      var bars = roots[r].querySelectorAll('.kaching-bundles__bar');
      for (var b = 0; b < bars.length; b++) decorateBar(bars[b]);

      var blocks = roots[r].querySelectorAll('.kaching-bundles__block');
      for (var k = 0; k < blocks.length; k++) decorateOneTime(blocks[k]);

      markHighlights(roots[r]);
    }
    return roots.length;
  }

  /* setTimeout, deliberately, NOT requestAnimationFrame (looks odd until you
     know why): rAF is throttled to a standstill in a hidden or background tab,
     so a page opened in a background tab rendered its bars with no unit-cost
     lines until it was focused. */
  function schedule() {
    if (pending) return;
    pending = true;
    window.setTimeout(run, 16);
  }

  /* Kaching re-renders its bars whenever a deal is selected or the buyer
     switches between subscribe and one-time, which wipes anything added here.
     run() is idempotent so a repaint that changes nothing produces no further
     mutations and the loop settles. */
  function watch() {
    var roots = document.querySelectorAll(ROOT);
    for (var i = 0; i < roots.length; i++) {
      if (watched) {
        if (watched.has(roots[i])) continue;
        watched.add(roots[i]);
      }
      new MutationObserver(schedule).observe(roots[i], {
        childList: true,
        subtree: true,
        characterData: true
      });
    }
    return roots.length;
  }

  function init() {
    watch();
    schedule();

    var body = document.body;
    if (!body) return;

    new MutationObserver(function () {
      if (watch()) schedule();
    }).observe(body, { childList: true, subtree: true });

    /* Belt and braces for the speed-optimiser app, which defers third-party JS
       until the first real gesture. */
    var tries = [300, 1000, 2500, 5000];
    for (var i = 0; i < tries.length; i++) {
      window.setTimeout(function () { if (watch()) schedule(); }, tries[i]);
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();